import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, inject, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../../core/auth/auth.service';
import { authRetryAfterSeconds } from '../../core/auth/auth-rate-limit.util';
import { LocalizationService } from '../../core/i18n/localization.service';
import { TranslatePipe } from '../../core/i18n/translate.pipe';

@Component({
  selector: 'app-password-completion-page',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, MatButtonModule, MatCardModule, MatFormFieldModule, MatIconModule, MatInputModule, MatProgressSpinnerModule, TranslatePipe],
  template: `
    <main class="completion-page"><mat-card><mat-card-content>
      <div class="brand"><div class="brand-mark">OS</div><h1>{{ titleKey() | t }}</h1></div>
      @if (!token) {
        <div class="error">{{ 'oauth.missingToken' | t }}</div>
        <a mat-flat-button color="primary" routerLink="/login">{{ 'oauth.backToLogin' | t }}</a>
      } @else {
        <p>{{ 'oauth.passwordCompletion.description' | t }}</p>
        <form [formGroup]="form" (ngSubmit)="submit()">
          <mat-form-field><mat-label>{{ 'oauth.passwordCompletion.newPassword' | t }}</mat-label>
            <input matInput type="password" autocomplete="new-password" formControlName="password" />
            @if (form.controls.password.hasError('minlength')) { <mat-error>{{ 'oauth.passwordCompletion.minLength' | t }}</mat-error> }
          </mat-form-field>
          <mat-form-field><mat-label>{{ 'oauth.passwordCompletion.confirmPassword' | t }}</mat-label>
            <input matInput type="password" autocomplete="new-password" formControlName="confirmation" />
          </mat-form-field>
          @if (passwordMismatch()) { <div class="error">{{ 'oauth.passwordCompletion.mismatch' | t }}</div> }
          @if (errorMessage()) { <div class="error">{{ errorMessage() }}</div> }
          <button mat-flat-button color="primary" type="submit" [disabled]="form.invalid || passwordMismatch() || loading() || retrySeconds() > 0">
            @if (loading()) { <mat-spinner diameter="20" /> } @else { <mat-icon>lock_reset</mat-icon> }
            @if (retrySeconds() > 0) { {{ 'oauth.passwordCompletion.retryIn' | t: { seconds: retrySeconds() } }} } @else { {{ 'oauth.passwordCompletion.submit' | t }} }
          </button>
        </form>
      }
    </mat-card-content></mat-card></main>
  `,
  styles: `
    :host{display:block;min-height:100dvh;background:linear-gradient(90deg,rgba(248,250,252,.9),rgba(248,250,252,.72)),url('/assets/login-background.svg') center/cover no-repeat}
    .completion-page{min-height:100dvh;display:grid;place-items:center;padding:24px} mat-card{width:min(460px,100%);background:rgba(255,255,255,.95)!important}
    .brand{display:flex;align-items:center;gap:14px;margin-bottom:18px}.brand-mark{display:grid;place-items:center;width:48px;height:48px;border-radius:8px;background:var(--app-accent);color:#fff;font-weight:900}
    h1,p{margin:0} p{color:var(--app-text-muted);margin-bottom:18px} form{display:grid;gap:12px} mat-form-field{width:100%} button{height:48px;display:inline-flex;gap:8px}.error{padding:10px 12px;border:1px solid #fecaca;border-radius:8px;color:#b91c1c;background:#fef2f2;font-weight:700;margin-bottom:12px}
  `
})
export class PasswordCompletionPageComponent implements OnDestroy {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private retryTimer: ReturnType<typeof setInterval> | null = null;
  token = this.takeTokenFromFragment();
  readonly loading = signal(false);
  readonly errorMessage = signal('');
  readonly retrySeconds = signal(0);
  readonly titleKey = signal(this.isInvitation() ? 'oauth.passwordCompletion.activationTitle' : 'oauth.passwordCompletion.resetTitle');
  readonly form = this.fb.group({
    password: this.fb.control('', [Validators.required, Validators.minLength(8)]),
    confirmation: this.fb.control('', Validators.required)
  });

  passwordMismatch(): boolean {
    const value = this.form.getRawValue();
    return !!value.confirmation && value.password !== value.confirmation;
  }

  async submit(): Promise<void> {
    if (!this.token || this.form.invalid || this.passwordMismatch() || this.loading() || this.retrySeconds() > 0) return;
    this.loading.set(true);
    this.errorMessage.set('');
    const password = this.form.controls.password.value;
    try {
      if (this.isInvitation()) await this.auth.acceptInvitation(this.token, password);
      else await this.auth.acceptPasswordReset(this.token, password);
      this.clearSensitiveState();
      await this.router.navigate(['/login'], { replaceUrl: true, state: { passwordCompleted: true } });
    } catch (error) {
      this.handleError(error);
    } finally {
      this.loading.set(false);
    }
  }

  ngOnDestroy(): void { this.clearSensitiveState(); if (this.retryTimer) clearInterval(this.retryTimer); }

  private isInvitation(): boolean { return this.route.snapshot.routeConfig?.path === 'activate-account'; }

  private takeTokenFromFragment(): string {
    const fragment = this.route.snapshot.fragment ?? window.location.hash.replace(/^#/, '');
    const fragmentQuery = fragment.includes('?') ? fragment.slice(fragment.indexOf('?') + 1) : fragment.replace(/^\?/, '');
    const token = new URLSearchParams(fragmentQuery).get('token')?.trim()
      || this.route.snapshot.queryParamMap.get('token')?.trim()
      || '';
    history.replaceState(history.state, document.title, `${location.pathname}${location.search}`);
    return token;
  }

  private clearSensitiveState(): void { this.token = ''; this.form.reset({ password: '', confirmation: '' }); }

  private handleError(error: unknown): void {
    if (error instanceof HttpErrorResponse && error.status === 410) {
      this.token = '';
      this.errorMessage.set(this.localization.translate('oauth.passwordCompletion.expired'));
      return;
    }
    if (error instanceof HttpErrorResponse && error.status === 429) {
      this.startRetryCountdown(authRetryAfterSeconds(error) ?? 60);
      this.errorMessage.set(this.localization.translate('oauth.passwordCompletion.throttled'));
      return;
    }
    this.errorMessage.set(this.localization.translate('oauth.passwordCompletion.failed'));
  }

  private startRetryCountdown(seconds: number): void {
    if (this.retryTimer) clearInterval(this.retryTimer);
    this.retrySeconds.set(seconds);
    this.retryTimer = setInterval(() => {
      this.retrySeconds.update((value) => Math.max(0, value - 1));
      if (this.retrySeconds() === 0 && this.retryTimer) { clearInterval(this.retryTimer); this.retryTimer = null; }
    }, 1000);
  }
}
